import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";
import { services } from "../mockData";

export const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Services | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Magic C Plumbing – reliable plumbing and heating in London SW18 & SW19. Leak repairs, bathrooms, boilers and landlord services. Call 07398 873176."
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Premium Services
            </h1>
            <p className="text-xl text-gray-300">
              Comprehensive plumbing solutions delivered with precision, expertise, and care. 
              From emergency repairs to complete renovations, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = require("lucide-react")[service.icon];
              const isEven = index % 2 === 0;

              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-8 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl h-96">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-6 left-6 bg-sage-500 p-4 rounded-xl">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="inline-block bg-sage-500/20 text-sage-300 px-4 py-2 rounded-full text-sm font-medium mb-4 backdrop-blur-md border border-sage-500/30">
                      Service #{String(index + 1).padStart(2, '0')}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={`${service.id}-feature-${idx}`} className="flex items-start space-x-3">
                          <CheckCircle className="h-6 w-6 text-sage-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-200">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white">
                        {index === 0 ? "Start Your Luxury Project" : index === 1 ? "Book a Consultation" : index === 2 ? "Request a Personalized Quote" : index === 3 ? "Transform Your Space Now" : index === 4 ? "Schedule a Site Visit" : "Contact Our Experts"}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Service Process
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Simple, transparent, and designed for your convenience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Contact Us",
                description: "Reach out via phone, WhatsApp, or our online form. We're available 24/7 for emergencies."
              },
              {
                step: "02",
                title: "Consultation",
                description: "We assess your needs and provide a detailed, transparent quote with no hidden fees."
              },
              {
                step: "03",
                title: "Expert Service",
                description: "Our skilled technicians arrive on time with all necessary tools and materials."
              },
              {
                step: "04",
                title: "Quality Guarantee",
                description: "We ensure your complete satisfaction and provide warranties on all our work."
              }
            ].map((item) => (
              <Card key={item.step} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-6">
                  <div className="text-6xl font-bold text-sage-400 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-300">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience Premium Service?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
            Get in touch today for a free consultation and discover why thousands trust us with their plumbing needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-lg px-8 py-6 h-auto">
              Get Your Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
